import { useEffect, useRef, useState } from "react";
import { Database, Download, Loader2, RotateCcw, ShieldAlert, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError, downloadFile, fmtWhen } from "@/lib/api";

const KIND = {
  manual: "taken by hand",
  scheduled: "taken on the schedule",
  safety: "safety copy before a restore",
};

/** The whole database, out and back in — the portal's own super-admins only. */
export const BackupPanel = () => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const [file, setFile] = useState(null);
  const [peek, setPeek] = useState(null);
  const [confirm, setConfirm] = useState("");
  const picker = useRef(null);

  const load = () =>
    api
      .get("/backup")
      .then((r) => setState(r.data))
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
  }, []);

  const takeNow = async () => {
    setBusy("take");
    try {
      const { data } = await api.post("/backup");
      toast.success(
        `Backup taken — ${data.collections} collection(s), ${data.documents} document(s), ${data.size_kb} KB.`,
      );
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const download = async (path, name) => {
    setBusy(path);
    try {
      await downloadFile(path, {}, name);
      toast.success("The backup is on its way down.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const choose = async (chosen) => {
    setFile(chosen);
    setPeek(null);
    setConfirm("");
    if (!chosen) return;
    setBusy("inspect");
    try {
      const body = new FormData();
      body.append("file", chosen);
      const { data } = await api.post("/backup/inspect", body);
      setPeek(data);
    } catch (e) {
      toast.error(apiError(e));
      setFile(null);
    } finally {
      setBusy("");
    }
  };

  const restore = async () => {
    setBusy("restore");
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("confirm", confirm);
      const { data } = await api.post("/backup/restore", body);
      toast.success(
        `Restored ${data.collections} collection(s), ${data.documents} document(s). A safety copy of what was here was kept first.`,
      );
      setFile(null);
      setPeek(null);
      setConfirm("");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mt-8 border border-border bg-card p-6" data-testid="backup-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-caps">
            <Database className="mr-1.5 inline h-3.5 w-3.5" />
            Database backup
          </div>
          <h2 className="mt-1 font-display text-lg font-bold">
            The whole portal, out and back in
          </h2>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">
            {state?.note ||
              "A backup is taken every night and kept in object storage for the super-admins."}{" "}
            Every collection travels in one gzipped file. Putting one back restores only what that
            file holds, and a safety copy of what is here now is taken first.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={!!busy}
            data-testid="backup-download-now"
            onClick={() =>
              download("/backup/download", "SAIL_DX_backup.json.gz")
            }
          >
            {busy === "/backup/download" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Download one now
          </Button>
          <Button disabled={!!busy} data-testid="backup-take-now" onClick={takeNow}>
            {busy === "take" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Database className="mr-2 h-4 w-4" />
            )}
            Take and keep a backup
          </Button>
        </div>
      </div>

      <div className="mt-5 overflow-x-auto border border-border">
        <table className="w-full min-w-[640px] text-sm" data-testid="backup-table">
          <thead className="bg-muted">
            <tr className="text-left">
              {["Backup", "Taken", "By", "Holds", ""].map((h) => (
                <th key={h} className="label-caps px-4 py-3">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(state?.backups || []).map((b) => (
              <tr key={b.id} data-testid={`backup-${b.id}`}>
                <td className="px-4 py-3">
                  <div className="font-data text-xs">{b.file_name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {KIND[b.kind] || b.kind} · {b.size_kb} KB
                  </div>
                </td>
                <td className="px-4 py-3 text-xs">{fmtWhen(b.taken_at)}</td>
                <td className="px-4 py-3 text-xs">{b.taken_by}</td>
                <td className="px-4 py-3 font-data text-xs tabular-nums">
                  {b.documents} docs · {b.collections} collections
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!!busy}
                    data-testid={`backup-download-${b.id}`}
                    onClick={() => download(`/backup/${b.id}/download`, b.file_name)}
                  >
                    <Download className="mr-2 h-3.5 w-3.5" /> Download
                  </Button>
                </td>
              </tr>
            ))}
            {!(state?.backups || []).length && (
              <tr>
                <td colSpan={5} className="p-6 text-center text-xs text-muted-foreground">
                  No backup has been kept yet. Press <b>Take and keep a backup</b>, or wait for
                  tonight's run.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 border border-[hsl(0_72%_42%)] p-4" data-testid="restore-block">
        <div className="label-caps text-[hsl(0_72%_42%)]">
          <ShieldAlert className="mr-1.5 inline h-3.5 w-3.5" />
          Put a backup back
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          Every collection the file holds is replaced by what is in it. Anything the file does not
          carry is left exactly as it is, and the backup list itself is never written over.
        </p>
        <input
          ref={picker}
          type="file"
          accept=".gz,.json"
          className="hidden"
          data-testid="restore-file"
          onChange={(e) => choose(e.target.files?.[0] || null)}
        />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            disabled={!!busy}
            data-testid="restore-choose"
            onClick={() => picker.current?.click()}
          >
            {busy === "inspect" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            Choose a backup file
          </Button>
          {peek && (
            <span className="font-data text-xs" data-testid="restore-peek">
              {peek.file_name} · taken {fmtWhen(peek.taken_at)} · {peek.collections} collections ·{" "}
              {peek.documents} documents
            </span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Input
            className="h-10 w-48 rounded-none"
            placeholder="Type RESTORE"
            value={confirm}
            disabled={!peek}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="restore-confirm"
          />
          <Button
            variant="destructive"
            disabled={!peek || !!busy || confirm.trim().toUpperCase() !== "RESTORE"}
            data-testid="restore-run"
            onClick={restore}
          >
              {busy === "restore" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
            Restore this backup
          </Button>
        </div>
      </div>
    </div>
  );
};
